import { useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Bot,
  CheckCircle2,
  Coins,
  Edit3,
  ListChecks,
  Maximize2,
  Mic,
  Minimize2,
  Plus,
  Save,
  Search,
  Trash2,
  Users
} from "lucide-react";
import {
  adminGrantCredits,
  adminSetTeknoparkAIEntitlement,
  deleteAIProcessDraft,
  deleteAIKnowledgeItem,
  ensureTeknoparkAIModule,
  getTenantAIState,
  saveAIKnowledgeItem,
  saveAIProcessDraft,
  subscribeAIKnowledgeItems,
  subscribeAIProcessDrafts,
  subscribeAITenants
} from "../services/teknoparkAIService";
import VoiceKnowledgeBuilder from "./VoiceKnowledgeBuilder";

const emptyForm = {
  id: "",
  question: "",
  alternativeQuestionsText: "",
  answer: "",
  category: "Genel",
  tagsText: "",
  sourceTitle: "4691 Teknopark Kanunu",
  sourceUrl: "",
  validFrom: "2000-01-01",
  validUntil: "2028-12-31",
  audience: "global",
  tenantIds: [],
  status: "draft"
};

const formatDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium" }).format(date);
};

const getErrorMessage = (error) =>
  error?.message?.replace(/^Firebase:\s*/i, "") || "İşlem tamamlanamadı.";

const AI_ADMIN_TAB_STORAGE_KEY = "argevio.teknoparkAI.adminTab";

function TeknoparkAIAdminView({ user, themeMode = "light" }) {
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "knowledge";
    const savedTab = window.localStorage.getItem(AI_ADMIN_TAB_STORAGE_KEY);
    return ["knowledge", "process-drafts", "licenses"].includes(savedTab) ? savedTab : "knowledge";
  });
  const [items, setItems] = useState([]);
  const [processDrafts, setProcessDrafts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantStates, setTenantStates] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [answerExpanded, setAnswerExpanded] = useState(false);
  const [showVoiceBuilder, setShowVoiceBuilder] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const dark = themeMode === "dark";
  const colors = {
    text: dark ? "#e5edf6" : "#123f50",
    muted: dark ? "#9fb0c4" : "#64748b",
    surface: dark ? "#162133" : "#ffffff",
    subtle: dark ? "#203047" : "#f8fafc",
    border: dark ? "#334155" : "#dbe2ea"
  };

  useEffect(() => {
    ensureTeknoparkAIModule().catch((reason) => setError(getErrorMessage(reason)));
    const stopKnowledge = subscribeAIKnowledgeItems(
      setItems,
      (reason) => setError(getErrorMessage(reason))
    );
    const stopProcessDrafts = subscribeAIProcessDrafts(
      setProcessDrafts,
      (reason) => setError(getErrorMessage(reason))
    );
    const stopTenants = subscribeAITenants(
      setTenants,
      (reason) => setError(getErrorMessage(reason))
    );
    return () => {
      stopKnowledge();
      stopProcessDrafts();
      stopTenants();
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(AI_ADMIN_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!answerExpanded) return undefined;
    const closeExpandedEditor = (event) => {
      if (event.key === "Escape") setAnswerExpanded(false);
    };
    window.addEventListener("keydown", closeExpandedEditor);
    return () => window.removeEventListener("keydown", closeExpandedEditor);
  }, [answerExpanded]);

  useEffect(() => {
    let active = true;
    if (!tenants.length) {
      setTenantStates({});
      return () => { active = false; };
    }
    Promise.all(
      tenants.map(async (tenant) => [tenant.id, await getTenantAIState(tenant.id)])
    ).then((entries) => {
      if (active) setTenantStates(Object.fromEntries(entries));
    }).catch((reason) => {
      if (active) setError(getErrorMessage(reason));
    });
    return () => { active = false; };
  }, [tenants]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("tr-TR");
    return items.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (!needle) return true;
      return [
        item.question,
        item.answer,
        item.category,
        ...(item.tags || [])
      ].join(" ").toLocaleLowerCase("tr-TR").includes(needle);
    });
  }, [items, search, statusFilter]);

  const categoryOptions = useMemo(() => (
    [...new Set(
      items.map((item) => String(item.category || "").trim()).filter(Boolean)
    )].sort((left, right) => left.localeCompare(right, "tr"))
  ), [items]);

  const tagOptions = useMemo(() => (
    [...new Set(
      items.flatMap((item) => Array.isArray(item.tags) ? item.tags : [])
        .map((tag) => String(tag || "").trim())
        .filter(Boolean)
    )].sort((left, right) => left.localeCompare(right, "tr"))
  ), [items]);

  const selectedTags = useMemo(() => (
    form.tagsText.split(",").map((tag) => tag.trim()).filter(Boolean)
  ), [form.tagsText]);

  const addTag = (value) => {
    const cleanTag = String(value || "").replace(/,$/, "").trim();
    if (!cleanTag) return;
    const alreadySelected = selectedTags.some(
      (tag) => tag.toLocaleLowerCase("tr-TR") === cleanTag.toLocaleLowerCase("tr-TR")
    );
    if (!alreadySelected) {
      setForm((current) => ({
        ...current,
        tagsText: [...selectedTags, cleanTag].join(", ")
      }));
    }
    setTagInput("");
  };

  const removeTag = (tagToRemove) => {
    setForm((current) => ({
      ...current,
      tagsText: selectedTags
        .filter((tag) => tag !== tagToRemove)
        .join(", ")
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setTagInput("");
    setAnswerExpanded(false);
    setShowForm(false);
  };

  const editItem = (item) => {
    setForm({
      ...emptyForm,
      ...item,
      alternativeQuestionsText: (item.alternativeQuestions || []).join("\n"),
      tagsText: (item.tags || []).join(", ")
    });
    setTagInput("");
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitKnowledge = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await saveAIKnowledgeItem({
        ...form,
        alternativeQuestions: form.alternativeQuestionsText
          .split("\n").map((value) => value.trim()).filter(Boolean),
        tags: form.tagsText
          .split(",").map((value) => value.trim()).filter(Boolean)
      }, { uid: user?.uid, email: user?.email });
      setMessage(form.id ? "Bilgi kaydı güncellendi." : "Yeni bilgi kaydı oluşturuldu.");
      resetForm();
    } catch (reason) {
      setError(getErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item) => {
    const confirmed = window.appConfirm
      ? await window.appConfirm(`“${item.question}” kaydı kalıcı olarak silinsin mi?`, {
        title: "Bilgi kaydını sil",
        type: "danger",
        confirmText: "Sil"
      })
      : window.confirm("Bilgi kaydı kalıcı olarak silinsin mi?");
    if (!confirmed) return;
    try {
      await deleteAIKnowledgeItem(item.id);
      setMessage("Bilgi kaydı silindi.");
    } catch (reason) {
      setError(getErrorMessage(reason));
    }
  };

  const removeProcessDraft = async (draft) => {
    const confirmed = window.appConfirm
      ? await window.appConfirm(`“${draft.title}” süreç taslağı kalıcı olarak silinsin mi?`, {
        title: "Süreç taslağını sil",
        type: "danger",
        confirmText: "Sil"
      })
      : window.confirm("Süreç taslağı kalıcı olarak silinsin mi?");
    if (!confirmed) return;
    try {
      await deleteAIProcessDraft(draft.id);
      setMessage("Süreç taslağı silindi.");
    } catch (reason) {
      setError(getErrorMessage(reason));
    }
  };

  const saveVoiceSessionDrafts = async (knowledgeDrafts, processDraft) => {
    setError("");
    await Promise.all(knowledgeDrafts.map((draft) => saveAIKnowledgeItem({
      ...draft,
      alternativeQuestions: draft.alternativeQuestions || [],
      tags: draft.tags || [],
      audience: "global",
      tenantIds: [],
      status: "draft"
    }, { uid: user?.uid, email: user?.email })));
    await saveAIProcessDraft(processDraft, { uid: user?.uid, email: user?.email });
    setShowVoiceBuilder(false);
    setActiveTab("process-drafts");
    setMessage(`${knowledgeDrafts.length} soru-cevap ve 1 süreç şablonu taslak olarak kaydedildi.`);
  };

  const refreshTenant = async (tenantId) => {
    const state = await getTenantAIState(tenantId);
    setTenantStates((current) => ({ ...current, [tenantId]: state }));
  };

  const grantCredits = async (tenant) => {
    const rawAmount = window.appPrompt
      ? await window.appPrompt(`${tenant.name || tenant.id} için tanımlanacak kredi miktarı`, {
        title: "Kredi tanımla",
        type: "info",
        confirmText: "Tanımla",
        inputPlaceholder: "Örn. 1000",
        defaultValue: "1000"
      })
      : window.prompt("Tanımlanacak kredi miktarı", "1000");
    if (!rawAmount) return;
    const amount = Math.trunc(Number(rawAmount));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Kredi miktarı sıfırdan büyük bir sayı olmalıdır.");
      return;
    }
    setBusy(true);
    setBusyAction(`credits-${tenant.id}`);
    setError("");
    setMessage("");
    try {
      await adminGrantCredits({
        tenantId: tenant.id,
        amount,
        note: "Yönetim panelinden kredi tanımlandı"
      });
      await refreshTenant(tenant.id);
      setMessage(`${tenant.name || tenant.id} için ${amount.toLocaleString("tr-TR")} kredi tanımlandı.`);
    } catch (reason) {
      setError(getErrorMessage(reason));
    } finally {
      setBusy(false);
      setBusyAction("");
    }
  };

  const setEntitlement = async (tenant, status) => {
    setBusy(true);
    setBusyAction(`entitlement-${tenant.id}`);
    setError("");
    setMessage("");
    try {
      await adminSetTeknoparkAIEntitlement({
        tenantId: tenant.id,
        status,
        durationDays: 30,
        questionLimit: 250
      });
      await refreshTenant(tenant.id);
      setMessage(
        status === "active"
          ? `${tenant.name || tenant.id} için Teknopark AI 30 günlüğüne etkinleştirildi.`
          : `${tenant.name || tenant.id} için Teknopark AI durduruldu.`
      );
    } catch (reason) {
      setError(getErrorMessage(reason));
    } finally {
      setBusy(false);
      setBusyAction("");
    }
  };

  return (
    <section style={{ color: colors.text }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 42, height: 42, borderRadius: 13, display: "grid", placeItems: "center", background: dark ? "#143d42" : "#e7fbf8", color: "#0f766e" }}><Bot size={22}/></span>
            <div>
              <h2 style={{ margin: 0 }}>Teknopark AI</h2>
              <p style={{ margin: "5px 0 0", color: colors.muted, fontWeight: 650 }}>Doğrulanmış cevap havuzu, müşteri lisansları ve krediler</p>
            </div>
          </div>
        </div>
        {activeTab === "knowledge" && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setShowVoiceBuilder(true)} style={secondaryButton(colors)}>
              <Mic size={17}/> Sesli bilgi oluştur
            </button>
            <button onClick={() => setShowForm((value) => !value)} style={primaryButton}>
              {showForm ? "Formu kapat" : <><Plus size={17}/> Yeni bilgi ekle</>}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "inline-flex", padding: 4, gap: 4, borderRadius: 13, background: colors.subtle, border: `1px solid ${colors.border}`, marginBottom: 18 }}>
        <button onClick={() => setActiveTab("knowledge")} style={tabButton(activeTab === "knowledge", dark)}><BookOpen size={17}/> Bilgi Havuzu</button>
        <button onClick={() => setActiveTab("process-drafts")} style={tabButton(activeTab === "process-drafts", dark)}><ListChecks size={17}/> Süreç Taslakları {processDrafts.length > 0 && `(${processDrafts.length})`}</button>
        <button onClick={() => setActiveTab("licenses")} style={tabButton(activeTab === "licenses", dark)}><Users size={17}/> Müşteri Lisansları</button>
      </div>

      {error && <div style={alertStyle("#fee2e2", "#991b1b")}>{error}</div>}
      {message && <div style={alertStyle(dark ? "#123f38" : "#dcfce7", dark ? "#a7f3d0" : "#166534")}>{message}</div>}

      {activeTab === "knowledge" && (
        <>
          {showForm && (
            <form onSubmit={submitKnowledge} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 20, marginBottom: 18 }}>
              <h3 style={{ margin: "0 0 16px" }}>{form.id ? "Bilgi kaydını düzenle" : "Yeni doğrulanmış cevap"}</h3>
              <div style={formGrid}>
                <label style={{ gridColumn: "1 / -1" }}>Ana soru
                  <input required value={form.question} onChange={(event) => setForm({ ...form, question: event.target.value })} style={inputStyle(colors)} placeholder="Örn. Ar-Ge personeli uzaktan çalışabilir mi?"/>
                </label>
                <label style={{ gridColumn: "1 / -1" }}>Alternatif soru biçimleri
                  <textarea value={form.alternativeQuestionsText} onChange={(event) => setForm({ ...form, alternativeQuestionsText: event.target.value })} style={{ ...inputStyle(colors), minHeight: 80, resize: "vertical" }} placeholder={"Her satıra bir alternatif soru\nUzaktan çalışma destek kapsamında mı?"}/>
                </label>
                <div
                  style={{
                    gridColumn: "1 / -1",
                    ...(answerExpanded
                      ? {
                        position: "fixed",
                        inset: 24,
                        zIndex: 2600,
                        display: "flex",
                        flexDirection: "column",
                        padding: 20,
                        borderRadius: 18,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        boxShadow: "0 28px 90px rgba(15,23,42,.35)"
                      }
                      : {})
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <label htmlFor="ai-verified-answer">Doğrulanmış cevap</label>
                    <button
                      type="button"
                      onClick={() => setAnswerExpanded((value) => !value)}
                      style={secondaryButton(colors)}
                    >
                      {answerExpanded
                        ? <><Minimize2 size={16}/> Editörü küçült</>
                        : <><Maximize2 size={16}/> Tam ekran düzenle</>}
                    </button>
                  </div>
                  <textarea
                    id="ai-verified-answer"
                    required
                    spellCheck
                    value={form.answer}
                    onChange={(event) => setForm({ ...form, answer: event.target.value })}
                    style={{
                      ...inputStyle(colors),
                      flex: answerExpanded ? 1 : undefined,
                      minHeight: answerExpanded ? 0 : 340,
                      marginTop: 8,
                      resize: answerExpanded ? "none" : "vertical",
                      padding: 15,
                      fontSize: 15,
                      lineHeight: 1.65
                    }}
                    placeholder="Müşteriye gösterilecek açık ve doğrulanmış cevap"
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, color: colors.muted, fontSize: 12 }}>
                    <span>Satır aralıkları ve paragraf düzeni müşteriye gösterilirken korunur.</span>
                    <span>
                      {form.answer.length.toLocaleString("tr-TR")} karakter · {Math.max(1, form.answer.split("\n").length)} satır
                    </span>
                  </div>
                </div>
                <label>Kategori
                  <input list="ai-category-options" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} style={inputStyle(colors)} placeholder="Mevcut kategoriyi seçin veya yenisini yazın"/>
                  <datalist id="ai-category-options">
                    {categoryOptions.map((category) => <option key={category} value={category}/>)}
                  </datalist>
                  <small style={{ display: "block", color: colors.muted, marginTop: 5 }}>Listeden seçebilir veya yeni kategori adı yazabilirsiniz.</small>
                </label>
                <div>
                  <label htmlFor="ai-tag-input">Etiketler</label>
                  {selectedTags.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
                      {selectedTags.map((tag) => (
                        <button key={tag} type="button" onClick={() => removeTag(tag)} title="Etiketi kaldır" style={selectedTagButton(dark)}>
                          {tag}<span aria-hidden="true">×</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <input
                    id="ai-tag-input"
                    list="ai-tag-options"
                    value={tagInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setTagInput(value);
                      const matchedTag = tagOptions.find(
                        (tag) => tag.toLocaleLowerCase("tr-TR") === value.trim().toLocaleLowerCase("tr-TR")
                      );
                      if (matchedTag) addTag(matchedTag);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === ",") {
                        event.preventDefault();
                        addTag(tagInput);
                      }
                    }}
                    onBlur={() => addTag(tagInput)}
                    style={inputStyle(colors)}
                    placeholder="Etiket seçin veya yenisini yazıp Enter'a basın"
                  />
                  <datalist id="ai-tag-options">
                    {tagOptions.filter((tag) => !selectedTags.includes(tag)).map((tag) => <option key={tag} value={tag}/>)}
                  </datalist>
                  {tagOptions.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 7 }}>
                      {tagOptions.filter((tag) => !selectedTags.includes(tag)).map((tag) => (
                        <button key={tag} type="button" onClick={() => addTag(tag)} style={suggestedTagButton(colors)}>{tag}</button>
                      ))}
                    </div>
                  )}
                </div>
                <label>Kaynak adı
                  <input value={form.sourceTitle} onChange={(event) => setForm({ ...form, sourceTitle: event.target.value })} style={inputStyle(colors)} placeholder="Kanun, yönetmelik veya iç rehber"/>
                </label>
                <label>Kaynak bağlantısı
                  <input type="url" value={form.sourceUrl} onChange={(event) => setForm({ ...form, sourceUrl: event.target.value })} style={inputStyle(colors)} placeholder="https://..."/>
                </label>
                <label>Geçerlilik başlangıcı
                  <input type="date" value={form.validFrom} onChange={(event) => setForm({ ...form, validFrom: event.target.value })} style={inputStyle(colors)}/>
                </label>
                <label>Geçerlilik bitişi
                  <input type="date" value={form.validUntil} onChange={(event) => setForm({ ...form, validUntil: event.target.value })} style={inputStyle(colors)}/>
                </label>
                <label>Görünürlük
                  <select value={form.audience} onChange={(event) => setForm({ ...form, audience: event.target.value, tenantIds: [] })} style={inputStyle(colors)}>
                    <option value="global">Tüm müşteriler</option>
                    <option value="tenant">Seçili müşteriler</option>
                  </select>
                </label>
                <label>Yayın durumu
                  <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })} style={inputStyle(colors)}>
                    <option value="draft">Taslak</option>
                    <option value="published">Yayında</option>
                  </select>
                </label>
                {form.audience === "tenant" && (
                  <fieldset style={{ gridColumn: "1 / -1", border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
                    <legend style={{ padding: "0 6px", fontWeight: 850 }}>Bu cevabı görebilecek müşteriler</legend>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      {tenants.map((tenant) => (
                        <label key={tenant.id} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                          <input
                            type="checkbox"
                            checked={form.tenantIds.includes(tenant.id)}
                            onChange={(event) => setForm({
                              ...form,
                              tenantIds: event.target.checked
                                ? [...form.tenantIds, tenant.id]
                                : form.tenantIds.filter((id) => id !== tenant.id)
                            })}
                          />
                          {tenant.name || tenant.id}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 18 }}>
                <button type="button" onClick={resetForm} style={secondaryButton(colors)}>Vazgeç</button>
                <button disabled={busy} type="submit" style={primaryButton}><Save size={17}/>{busy ? "Kaydediliyor..." : "Kaydet"}</button>
              </div>
            </form>
          )}

          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 16 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              <label style={{ flex: "1 1 280px", position: "relative" }}>
                <Search size={17} style={{ position: "absolute", left: 12, top: 12, color: colors.muted }}/>
                <input value={search} onChange={(event) => setSearch(event.target.value)} style={{ ...inputStyle(colors), paddingLeft: 38, marginTop: 0 }} placeholder="Soru, kategori veya etikette ara..."/>
              </label>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={{ ...inputStyle(colors), width: 160, marginTop: 0 }}>
                <option value="all">Tüm durumlar</option>
                <option value="published">Yayında</option>
                <option value="draft">Taslak</option>
              </select>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {filteredItems.map((item) => (
                <article key={item.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 14, padding: 15, background: colors.subtle }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 7 }}>
                        <span style={badge(item.status === "published" ? "#dcfce7" : "#fef3c7", item.status === "published" ? "#166534" : "#92400e")}>{item.status === "published" ? "Yayında" : "Taslak"}</span>
                        <span style={badge(dark ? "#29374c" : "#e2e8f0", colors.text)}>{item.category || "Genel"}</span>
                        <span style={badge(dark ? "#29374c" : "#e2e8f0", colors.text)}>{item.audience === "tenant" ? "Seçili müşteriler" : "Tüm müşteriler"}</span>
                      </div>
                      <strong style={{ fontSize: 15 }}>{item.question}</strong>
                      <p style={{ margin: "7px 0 0", color: colors.muted, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{item.answer}</p>
                      {(item.sourceTitle || item.sourceUrl) && <small style={{ display: "block", marginTop: 9, color: colors.muted }}>Kaynak: {item.sourceTitle || item.sourceUrl}</small>}
                    </div>
                    <div style={{ display: "flex", gap: 7, flexShrink: 0 }}>
                      <button onClick={() => editItem(item)} title="Düzenle" style={iconButton(colors)}><Edit3 size={16}/></button>
                      <button onClick={() => removeItem(item)} title="Sil" style={{ ...iconButton(colors), color: "#dc2626" }}><Trash2 size={16}/></button>
                    </div>
                  </div>
                </article>
              ))}
              {!filteredItems.length && <div style={{ padding: 32, textAlign: "center", color: colors.muted }}>Bu filtreye uygun bilgi kaydı bulunamadı.</div>}
            </div>
          </div>
        </>
      )}

      {activeTab === "process-drafts" && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ padding: 14, borderRadius: 14, background: dark ? "#172d3b" : "#ecfeff", border: `1px solid ${dark ? "#285467" : "#a5f3fc"}`, color: colors.text }}>
            Konuşmalardan çıkarılan süreçler burada taslak olarak tutulur. Müşterinin aylık süreçlerine aktarılmadan önce adımlar, tarihler ve sorumlular ayrıca kontrol edilmelidir.
          </div>
          {processDrafts.map((draft) => (
            <article key={draft.id} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 17 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 8 }}>
                    <span style={badge("#fef3c7", "#92400e")}>Taslak</span>
                    <span style={badge(dark ? "#29374c" : "#e2e8f0", colors.text)}>{draft.category || "Genel"}</span>
                    <span style={badge(dark ? "#29374c" : "#e2e8f0", colors.text)}>{draft.steps?.length || 0} adım</span>
                  </div>
                  <h3 style={{ margin: 0 }}>{draft.title}</h3>
                  {draft.overview && <p style={{ margin: "8px 0", color: colors.muted, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{draft.overview}</p>}
                  {draft.deadline && <div style={{ margin: "8px 0", padding: 10, borderRadius: 10, background: dark ? "#1d3144" : "#f0f9ff", whiteSpace: "pre-wrap" }}><strong>Süre / tekrar:</strong> {draft.deadline}</div>}
                  <ol style={{ margin: "12px 0 0", paddingLeft: 22, display: "grid", gap: 7 }}>
                    {(draft.steps || []).map((step, index) => <li key={`${draft.id}-${index}`}>{step.title}</li>)}
                  </ol>
                  {draft.sourceTitle && <small style={{ display: "block", marginTop: 11, color: colors.muted }}>Kaynak: {draft.sourceTitle}</small>}
                </div>
                <button onClick={() => removeProcessDraft(draft)} title="Sil" style={{ ...iconButton(colors), color: "#dc2626", flexShrink: 0 }}><Trash2 size={16}/></button>
              </div>
            </article>
          ))}
          {!processDrafts.length && <div style={{ padding: 36, textAlign: "center", color: colors.muted, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18 }}>Henüz süreç taslağı oluşturulmadı. “Sesli bilgi oluştur” ile yeni bir bilgi oturumu başlatabilirsiniz.</div>}
        </div>
      )}

      {activeTab === "licenses" && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ padding: 14, borderRadius: 14, background: dark ? "#172d3b" : "#ecfeff", border: `1px solid ${dark ? "#285467" : "#a5f3fc"}`, color: colors.text }}>
            Kredi yükleme şimdilik yalnızca yönetim panelinden yapılır. Her hareket değiştirilemeyen kredi kayıt defterine yazılır; ödeme sağlayıcısı daha sonra bu katmana bağlanacaktır.
          </div>
          {tenants.map((tenant) => {
            const state = tenantStates[tenant.id] || {};
            const entitlement = state.entitlement;
            const active = entitlement?.status === "active" &&
              (!entitlement.expiresAt?.toDate || entitlement.expiresAt.toDate().getTime() > Date.now());
            return (
              <article key={tenant.id} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 17 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{tenant.name || tenant.id}</h3>
                    <div style={{ color: colors.muted, fontSize: 13, marginTop: 5 }}>{tenant.hostname || tenant.slug || tenant.id}</div>
                  </div>
                  <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
                    <div><small style={{ color: colors.muted }}>Kredi bakiyesi</small><strong style={{ display: "block", marginTop: 3, fontSize: 18 }}>{Number(state.wallet?.balance || 0).toLocaleString("tr-TR")}</strong></div>
                    <div><small style={{ color: colors.muted }}>Modül</small><strong style={{ display: "flex", gap: 5, alignItems: "center", marginTop: 3, color: active ? "#0f766e" : colors.muted }}>{active && <CheckCircle2 size={16}/>} {active ? "Aktif" : "Kapalı"}</strong></div>
                    <div><small style={{ color: colors.muted }}>Bitiş</small><strong style={{ display: "block", marginTop: 3 }}>{formatDate(entitlement?.expiresAt)}</strong></div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button disabled={busy} onClick={() => grantCredits(tenant)} style={secondaryButton(colors)}>
                        <Coins size={16}/>{busyAction === `credits-${tenant.id}` ? "Kredi tanımlanıyor..." : "Kredi tanımla"}
                      </button>
                      <button disabled={busy} onClick={() => setEntitlement(tenant, active ? "inactive" : "active")} style={active ? dangerButton : primaryButton}>
                        {busyAction === `entitlement-${tenant.id}` ? "Güncelleniyor..." : active ? "Modülü durdur" : "30 gün etkinleştir"}
                      </button>
                    </div>
                  </div>
                </div>
                {state.ledger?.length > 0 && (
                  <details style={{ marginTop: 13, color: colors.muted }}>
                    <summary style={{ cursor: "pointer", fontWeight: 800 }}>Son kredi hareketleri ({state.ledger.length})</summary>
                    <div style={{ display: "grid", gap: 6, marginTop: 9 }}>
                      {state.ledger.map((entry) => (
                        <div key={entry.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: 9, borderRadius: 9, background: colors.subtle }}>
                          <span>{entry.note || entry.type}</span>
                          <strong style={{ color: Number(entry.amount) >= 0 ? "#0f766e" : "#dc2626" }}>{Number(entry.amount) >= 0 ? "+" : ""}{Number(entry.amount || 0).toLocaleString("tr-TR")}</strong>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </article>
            );
          })}
          {!tenants.length && <div style={{ padding: 32, textAlign: "center", color: colors.muted }}>Henüz müşteri kaydı yok.</div>}
        </div>
      )}

      {showVoiceBuilder && (
        <VoiceKnowledgeBuilder
          themeMode={themeMode}
          categoryOptions={categoryOptions}
          onClose={() => setShowVoiceBuilder(false)}
          onUseDraft={(draft) => {
            setForm({
              ...emptyForm,
              ...draft,
              alternativeQuestionsText: (draft.alternativeQuestions || []).join("\n"),
              tagsText: (draft.tags || []).join(", ")
            });
            setTagInput("");
            setShowVoiceBuilder(false);
            setShowForm(true);
            setMessage("Sesli soru-cevap taslağı bilgi formuna aktarıldı. Kontrol edip yayımlayabilirsiniz.");
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
          }}
          onSaveDrafts={saveVoiceSessionDrafts}
        />
      )}
    </section>
  );
}

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 13
};
const inputStyle = (colors) => ({
  width: "100%",
  boxSizing: "border-box",
  marginTop: 6,
  borderRadius: 10,
  border: `1px solid ${colors.border}`,
  background: colors.subtle,
  color: colors.text,
  padding: "11px 12px",
  font: "inherit"
});
const primaryButton = {
  border: 0,
  borderRadius: 10,
  padding: "10px 13px",
  background: "#0f766e",
  color: "#fff",
  fontWeight: 850,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  cursor: "pointer"
};
const dangerButton = { ...primaryButton, background: "#b91c1c" };
const secondaryButton = (colors) => ({
  ...primaryButton,
  background: colors.subtle,
  color: colors.text,
  border: `1px solid ${colors.border}`
});
const iconButton = (colors) => ({
  width: 35,
  height: 35,
  borderRadius: 9,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.text,
  display: "grid",
  placeItems: "center",
  cursor: "pointer"
});
const tabButton = (active, dark) => ({
  border: 0,
  borderRadius: 9,
  padding: "9px 12px",
  background: active ? "#0f766e" : "transparent",
  color: active ? "#fff" : dark ? "#d8e4ef" : "#124254",
  fontWeight: 850,
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  cursor: "pointer"
});
const badge = (background, color) => ({
  display: "inline-flex",
  padding: "4px 8px",
  borderRadius: 999,
  background,
  color,
  fontSize: 11,
  fontWeight: 850
});
const selectedTagButton = (dark) => ({
  border: 0,
  borderRadius: 999,
  padding: "5px 9px",
  background: dark ? "#134e4a" : "#ccfbf1",
  color: dark ? "#ccfbf1" : "#115e59",
  fontWeight: 800,
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer"
});
const suggestedTagButton = (colors) => ({
  border: `1px solid ${colors.border}`,
  borderRadius: 999,
  padding: "4px 8px",
  background: colors.subtle,
  color: colors.muted,
  fontWeight: 750,
  fontSize: 11,
  cursor: "pointer"
});
const alertStyle = (background, color) => ({
  padding: "11px 13px",
  borderRadius: 11,
  background,
  color,
  marginBottom: 13,
  fontWeight: 700
});

export default TeknoparkAIAdminView;
